import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  DragAndDropPlanSavedMessage,
  OptimizationStoppedToastMessage,
  WebSocketMessagePayload,
} from "../hooks/WebSocketProvider";
import { decodeJobId } from "../store/realTimeUpdates/helpers";
import { getGanttChartData } from "../store/ganttChart/action";
import { setNotification } from "../store/toastNotifications";
import { getSolutionType } from "../utils/fetchQueryUtil";
import labels from "../utils/labels";

export class WebsocketReactivityService {
  private readonly dispatch: ThunkDispatch<any, any, any>;

  constructor(dispatch: ThunkDispatch<any, any, any>) {
    this.dispatch = dispatch;
  }

  handleMessage = (message: WebSocketMessagePayload) => {
    switch (message.bridgeMessageType) {
      case "OptimizationStoppedToastMessage":
        this.handleOptimizationStoppedMessage(message);
        break;
      case "DragAndDropPlanSaved":
        this.handleDragAndDropPlanSaved(message);
        break;
    }
  };

  /**
   * When a user saves a drag-and-drop plan, all connected clients viewing the same
   * planning date need to refetch so they see the persisted changes.
   *
   * This dispatches getGanttChartData directly rather than going through the
   * realTimeUpdateSlice state machine, because the normal fetch path in
   * useFetchOnQueryChange is guarded by `!dataToUse` — which blocks refetches
   * when gantt chart data is already loaded.
   *
   * Only refetches if the client is currently viewing the same filter/date that
   * was saved on the PondooSolution (Optimised) tab, to avoid overwriting
   * unrelated data in the gantt chart slice.
   */
  private handleDragAndDropPlanSaved = (
    message: DragAndDropPlanSavedMessage,
  ) => {
    const params = new URLSearchParams(window.location.search);
    const currentFilter = params.get("filter");
    const currentDate = params.get("date");

    if (
      currentFilter !== String(message.data.idActiveNexusFilter) ||
      currentDate?.split("T")[0] !== message.data.planningDate.split("T")[0]
    ) {
      return;
    }

    this.dispatch((dispatch: ThunkDispatch<any, any, any>, getState: () => any) => {
      const { selectedGanttChartTab } = getState().ganttChartSlice;
      if (getSolutionType(selectedGanttChartTab) !== "PondooSolution") {
        return;
      }

      dispatch(
        getGanttChartData({
          solutionType: "PondooSolution",
          filterId: message.data.idActiveNexusFilter,
          date: message.data.planningDate,
        }),
      );
    });
  };

  private handleOptimizationStoppedMessage = (
    message: OptimizationStoppedToastMessage,
  ) => {
    const { date } = decodeJobId(message.jobId);
    this.dispatch(
      setNotification({
        type: "optimization_stopped",
        message: labels.stopOptimizationToastMessage(date),
      }),
    );
  };
}
